import React from 'react';

interface Props {
  onGetStarted: () => void;
}

export function LandingPage({ onGetStarted }: Props) {
  return (
    <div className="min-h-screen bg-sand-50">
      {/* Top accent */}
      <div className="h-1 bg-gradient-to-r from-adventure-light via-adventure to-adventure-dark" />

      {/* Nav */}
      <nav className="max-w-6xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-adventure/10 flex items-center justify-center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-adventure">
              <path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"/><path d="M9 3v14"/><path d="M15 7v14"/>
            </svg>
          </div>
          <span className="font-display text-xl text-sand-900">Adventure Editor</span>
        </div>
        <button onClick={onGetStarted} className="btn-primary text-sm">
          Get Started
        </button>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-20 text-center">
        <div className="inline-block bg-adventure/10 text-adventure-dark text-sm font-medium px-4 py-1.5 rounded-full mb-6">
          Free 14-day trial — no credit card required
        </div>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-sand-900 max-w-3xl mx-auto leading-tight">
          Turn your travel photos into beautiful shareable stories
        </h1>

        <p className="text-sand-500 text-lg sm:text-xl max-w-2xl mx-auto mt-6 leading-relaxed">
          Organize your adventures day by day, upload your photos, and publish a stunning website — all in minutes. Share with family and friends, or keep as your private journal.
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
          <button onClick={onGetStarted}
            className="btn-primary text-lg px-8 py-4 justify-center">
            Start Your Free Trial
          </button>
          <a href="#how-it-works"
            className="btn-secondary text-lg px-8 py-4 justify-center">
            See How It Works
          </a>
        </div>

        {/* Hero visual */}
        <div className="mt-16 max-w-4xl mx-auto">
          <div className="card p-3 sm:p-4 shadow-xl">
            <div className="bg-sand-100 rounded-lg overflow-hidden">
              {/* Mock dashboard */}
              <div className="bg-white border-b border-sand-200 px-4 py-3 flex items-center gap-3">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-amber-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 bg-sand-100 rounded h-5 max-w-xs mx-auto" />
              </div>
              <div className="p-6 flex gap-6">
                <div className="w-48 space-y-2 hidden sm:block">
                  <div className="bg-adventure/10 rounded-lg px-3 py-2.5">
                    <div className="font-medium text-adventure-dark text-sm">2025 Japan</div>
                    <div className="text-xs text-sand-400 mt-0.5">16 days · 706 photos</div>
                  </div>
                  <div className="bg-white rounded-lg px-3 py-2.5">
                    <div className="text-sand-700 text-sm">2025 Morocco</div>
                    <div className="text-xs text-sand-400 mt-0.5">8 days · 342 photos</div>
                  </div>
                  <div className="bg-white rounded-lg px-3 py-2.5">
                    <div className="text-sand-700 text-sm">2024 Italy</div>
                    <div className="text-xs text-sand-400 mt-0.5">12 days · 498 photos</div>
                  </div>
                </div>
                <div className="flex-1 space-y-3">
                  <div className="h-6 bg-sand-200 rounded w-48" />
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {Array.from({ length: 8 }).map((_, i) => (
                      <div key={i} className="aspect-square rounded-lg" style={{
                        background: [
                          'linear-gradient(135deg, #E8D5B7, #C4A882)',
                          'linear-gradient(135deg, #87CEEB, #5BA3CF)',
                          'linear-gradient(135deg, #98D8C8, #6BB5A5)',
                          'linear-gradient(135deg, #F4A460, #D2893C)',
                          'linear-gradient(135deg, #DDA0DD, #BB7DB8)',
                          'linear-gradient(135deg, #90EE90, #6FC96F)',
                          'linear-gradient(135deg, #FFB6C1, #E8949E)',
                          'linear-gradient(135deg, #F0E68C, #D4CC6A)',
                        ][i]
                      }} />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="bg-white border-y border-sand-200 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl sm:text-4xl text-sand-900 text-center mb-4">
            Three steps to your adventure story
          </h2>
          <p className="text-sand-500 text-center max-w-xl mx-auto mb-16">
            No design skills needed. Just your photos and your memories.
          </p>

          <div className="grid sm:grid-cols-3 gap-10 sm:gap-8">
            {[
              {
                step: '1',
                title: 'Create your adventure',
                desc: 'Name your trip and organize it into days. Japan 2025, Morocco road trip, family holiday — whatever your story is.',
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-adventure">
                    <path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"/>
                  </svg>
                ),
              },
              {
                step: '2',
                title: 'Upload your photos',
                desc: 'Drag and drop photos into each day. Add captions and descriptions. Reorder until it tells your story.',
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-adventure">
                    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
                  </svg>
                ),
              },
              {
                step: '3',
                title: 'Publish & share',
                desc: 'One click generates a beautiful website. Share the link with family, friends, or the world.',
                icon: (
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-adventure">
                    <circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z"/>
                  </svg>
                ),
              },
            ].map((item) => (
              <div key={item.step} className="text-center sm:text-left">
                <div className="w-14 h-14 rounded-2xl bg-adventure/10 flex items-center justify-center mx-auto sm:mx-0 mb-4">
                  {item.icon}
                </div>
                <h3 className="font-display text-xl text-sand-900 mb-2">{item.title}</h3>
                <p className="text-sand-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl sm:text-4xl text-sand-900 text-center mb-16">
            Everything you need
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Unlimited photos', desc: 'Upload as many photos as you want. No storage limits on Pro plans.' },
              { title: 'Beautiful websites', desc: 'Auto-generated, mobile-friendly pages hosted on our fast global CDN.' },
              { title: 'Family sharing', desc: 'Share access with family members using a simple transfer code.' },
              { title: 'Works everywhere', desc: 'Full-featured web app that works on desktop, tablet, and phone.' },
              { title: 'Your photos, your data', desc: 'Export anytime. No lock-in. We host your photos securely on AWS.' },
              { title: 'Fast & reliable', desc: 'Built on AWS with CloudFront CDN. Your pages load fast, everywhere.' },
            ].map((f) => (
              <div key={f.title} className="card p-6">
                <h3 className="font-medium text-sand-900 mb-2">{f.title}</h3>
                <p className="text-sand-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-white border-y border-sand-200 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-display text-3xl sm:text-4xl text-sand-900 text-center mb-4">
            Simple pricing
          </h2>
          <p className="text-sand-500 text-center max-w-xl mx-auto mb-12">
            Start free for 14 days. No credit card required.
          </p>

          <div className="max-w-sm mx-auto">
            <div className="card overflow-hidden shadow-lg">
              <div className="bg-adventure/5 px-6 py-8 text-center border-b border-sand-200">
                <div className="text-sm font-medium text-adventure uppercase tracking-wide mb-2">Pro</div>
                <div className="flex items-baseline justify-center gap-1">
                  <span className="font-display text-5xl text-sand-900">$9</span>
                  <span className="text-sand-500">/month</span>
                </div>
                <p className="text-sand-400 text-sm mt-2">14-day free trial</p>
              </div>

              <div className="px-6 py-6">
                <ul className="space-y-3">
                  {[
                    'Unlimited adventures',
                    'Unlimited photo uploads',
                    'Custom published website',
                    'Family sharing',
                    'Priority support',
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-3 text-sm text-sand-700">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-green-500 flex-shrink-0">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>

                <button onClick={onGetStarted}
                  className="btn-primary w-full justify-center py-3 mt-6">
                  Start Free Trial
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-sand-900 mb-4">
            Ready to tell your story?
          </h2>
          <p className="text-sand-500 text-lg mb-8">
            Start your free trial today. Your adventures deserve to be shared.
          </p>
          <button onClick={onGetStarted}
            className="btn-primary text-lg px-8 py-4 justify-center">
            Get Started — It's Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-sand-200 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-adventure/10 flex items-center justify-center">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-adventure">
                <path d="M3 7l6-4 6 4 6-4v14l-6 4-6-4-6 4V7z"/>
              </svg>
            </div>
            <span className="text-sand-500 text-sm">Adventure Editor</span>
          </div>
          <p className="text-sand-400 text-sm">
            &copy; {new Date().getFullYear()} Adventure Editor. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
